import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQC } from './QCContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Clock,
  TrendingUp,
  Briefcase,
  CheckCircle2,
  MapPin,
  Calendar,
  Timer,
  Key,
  Lock,
  Bell,
  AlertTriangle,
  X,
} from 'lucide-react';
import { QCOvertimeList } from './QCOvertimeList';

export const QCEmployeeDashboard = () => {
  const { user, api, logout } = useQC();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    overtime_hours: 0,
    ranking: 1,
    pending_offers: 0,
    accepted_jobs: 0,
  });
  const [jobs, setJobs] = useState([]);
  const [pendingOffers, setPendingOffers] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({});
  
  // Password dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 200);
    } catch (e) {
      console.log('Audio notification not supported');
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (updated[key] > 0) updated[key] -= 1;
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      // Fetch jobs from GETGRIDGATE
      const jobsRes = await api.get('/canvass/jobs').catch(() => ({ data: [] }));
      const openJobs = (jobsRes.data || []).filter(j => j.status === 'open' || j.status === 'in_progress');
      setJobs(openJobs);

      // Fetch my assignments (includes pending offers)
      const assignmentsRes = await api.get('/my/assignments').catch(() => ({ data: [] }));
      const assignments = assignmentsRes.data || [];
      setMyAssignments(assignments);
      
      // Filter pending offers (pending acknowledgements)
      const pending = assignments.filter(a => a.acknowledgement?.status === 'pending');
      setPendingOffers(pending);

      // Update time remaining for pending offers
      const newTimeRemaining = {};
      pending.forEach(offer => {
        if (offer.time_remaining) {
          newTimeRemaining[offer.id] = offer.time_remaining;
        }
      });
      setTimeRemaining(newTimeRemaining);

      // Fetch bid order to get user ranking
      const bidOrderRes = await api.get('/canvass/bid-order').catch(() => ({ data: [] }));
      const bidOrder = bidOrderRes.data || [];
      const userRank = bidOrder.findIndex(u => u.user_id === user?.id) + 1;
      const userData = bidOrder.find(u => u.user_id === user?.id);

      setStats({
        overtime_hours: userData?.overtime_hours || 0,
        ranking: userRank || bidOrder.length + 1,
        pending_offers: pending.length,
        accepted_jobs: assignments.filter(a => a.acknowledgement?.status === 'acknowledged').length,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleOfferResponse = async (assignmentId, ackId, accept) => {
    try {
      if (accept) {
        await api.post(`/assignments/${ackId}/acknowledge`);
        toast.success('Job accepted!');
      } else {
        // For decline, navigate to decline flow or show reason selector
        navigate(`/quickcanvass/decline/${ackId}`);
        return;
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  const formatTimeRemaining = (offerId, seconds) => {
    const secs = timeRemaining[offerId] ?? seconds ?? 0;
    if (secs <= 0) return <span className="text-red-600 font-bold">Expired</span>;
    const mins = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    const timeStr = `${mins}m ${s}s`;
    
    if (secs < 60) return <span className="text-red-600 font-bold animate-pulse">{timeStr}</span>;
    if (secs < 300) return <span className="text-orange-600 font-bold">{timeStr}</span>;
    return <span className="font-mono">{timeStr}</span>;
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully!');
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="qc-employee-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight">
            Welcome, {user?.name}
          </h1>
          <p className="text-muted-foreground mt-2">Your overtime dashboard</p>
        </div>
        
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <Button variant="outline" className="gap-2" onClick={() => setPasswordDialogOpen(true)}>
            <Key className="h-4 w-4" />
            Change Password
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </DialogTitle>
              <DialogDescription>
                Update your login password. Choose a strong password with at least 6 characters.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Current Password</Label>
                <Input 
                  type="password" 
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input 
                  type="password" 
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm New Password</Label>
                <Input 
                  type="password" 
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={changingPassword}>
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Overtime Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{stats.overtime_hours}</div>
            <p className="text-xs text-muted-foreground mt-1">Total accumulated</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Current Ranking
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">#{stats.ranking}</div>
            <p className="text-xs text-muted-foreground mt-1">Lower is better</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Timer className="h-4 w-4" />
              Pending Offers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{stats.pending_offers}</div>
            <p className="text-xs text-muted-foreground mt-1">Awaiting response</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Accepted Jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black">{stats.accepted_jobs}</div>
            <p className="text-xs text-muted-foreground mt-1">Completed/Upcoming</p>
          </CardContent>
        </Card>
      </div>

      {/* Overtime List */}
      <QCOvertimeList currentUserId={user?.id} />

      {/* Pending Offers */}
      {pendingOffers.length > 0 && (
        <Card className="border-2 border-orange-500 shadow-lg">
          <CardHeader className="bg-orange-50 dark:bg-orange-950/20">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Bell className="h-6 w-6 text-orange-500 animate-bounce" />
              Pending Job Offers
              <Badge variant="destructive" className="ml-2">{pendingOffers.length} NEW</Badge>
            </CardTitle>
            <CardDescription>⚠️ Respond quickly! Offers will expire and go to the next employee.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {pendingOffers.map((offer) => {
              const seconds = timeRemaining[offer.id] || offer.time_remaining || 0;
              const isUrgent = seconds < 300;
              const isCritical = seconds < 60;
              
              return (
                <div
                  key={offer.id}
                  className={`p-6 border-2 rounded-lg transition-all ${
                    isCritical ? 'border-red-500 bg-red-50 dark:bg-red-950/20 animate-pulse' :
                    isUrgent ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20' :
                    'border-border bg-card hover:border-orange-500'
                  }`}
                  data-testid={`pending-offer-${offer.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div>
                        <h3 className="text-xl font-bold">{offer.job_title}</h3>
                        <Badge variant="secondary">{offer.crew_role_name}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {offer.job_location && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            {offer.job_location}
                          </div>
                        )}
                        {offer.job_start_date && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {offer.job_start_date}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge variant={isCritical ? "destructive" : isUrgent ? "default" : "outline"} className="font-mono text-base px-3 py-1">
                          <Timer className="h-4 w-4 mr-1" />
                          {formatTimeRemaining(offer.id, seconds)}
                        </Badge>
                        {isCritical && <span className="text-red-600 text-sm font-semibold">Respond NOW!</span>}
                      </div>
                    </div>

                    <div className="flex md:flex-col gap-3">
                      <Button
                        onClick={() => handleOfferResponse(offer.id, offer.acknowledgement?.id, true)}
                        className="flex-1 md:w-36 h-14 font-bold text-lg bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`accept-offer-${offer.id}`}
                      >
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                        ACCEPT
                      </Button>
                      <Button
                        onClick={() => handleOfferResponse(offer.id, offer.acknowledgement?.id, false)}
                        variant="outline"
                        className="flex-1 md:w-36 h-12"
                        data-testid={`decline-offer-${offer.id}`}
                      >
                        DECLINE
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Available Jobs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">Available Jobs</h2>
          <Button onClick={() => navigate('/canvass')} className="gap-2">
            <Briefcase className="h-4 w-4" />
            Go to Draft Board
          </Button>
        </div>

        {jobs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No available jobs at the moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {jobs.slice(0, 6).map((job) => (
              <Card key={job.id} className="hover:border-primary transition-colors cursor-pointer" onClick={() => navigate(`/canvass`)}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{job.title}</CardTitle>
                    {job.status === 'in_progress' && <Badge>In Progress</Badge>}
                  </div>
                  <CardDescription className="space-y-2 mt-2">
                    {job.start_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {job.start_date}
                      </div>
                    )}
                    {job.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </div>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Badge variant={job.status === 'open' ? 'default' : 'secondary'}>
                    {job.status}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* My Assignments */}
      {myAssignments.filter(a => a.acknowledgement?.status === 'acknowledged').length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-4">My Assignments</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {myAssignments.filter(a => a.acknowledgement?.status === 'acknowledged').map((assignment) => (
              <Card key={assignment.id} className="border-l-4 border-l-green-500">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{assignment.job_title}</CardTitle>
                    <Badge className="bg-green-600">Active</Badge>
                  </div>
                  <CardDescription className="space-y-1 mt-2">
                    {assignment.job_location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {assignment.job_location}
                      </div>
                    )}
                    <Badge variant="secondary">{assignment.crew_role_name}</Badge>
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QCEmployeeDashboard;
